/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import NextImage from 'next/image';
import React, { forwardRef } from 'react';
import { useAboveFoldContext } from '@/libraries/contexts/above-fold-context';
import { formatImageUrl } from '@/libraries/formatters/format-image-url';
import { getFocalPointObjectPosition } from '@/libraries/getters/get-focal-point-object-position';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import styles from './image.module.scss';

export interface ImageProps {
  /** The text to show as an alternative to the image */
  alt: string;
  /** The aspect ratio to use for the image */
  aspectRatio?: Content.Properties.AspectRatio;
  /** The class name to apply */
  className?: string;
  /** Indicates whether the image should fill its container */
  fill?: boolean;
  /** The focal point to use for the image */
  focalPoint?: FocalPoint | null;
  /** The height of the image */
  height?: number;
  /** The object fit property of the image */
  objectfit?: string;
  /** Indicates whether the image should be loaded immediately */
  priority?: boolean;
  /** The sizes of the image for different screen sizes */
  sizes?: string;
  /** The source url of the image */
  src: string;
  /** The source set of image sizes */
  srcSet?: {
    desktop?: {
      height?: number;
      width: number;
    };
    laptop?: {
      height?: number;
      width: number;
    };
    mobile?: {
      height?: number;
      width: number;
    };
    tablet?: {
      height?: number;
      width: number;
    };
  };
  /** CSS style properties of the image  */
  style?: React.CSSProperties;
  /** A title to show when hovering the image */
  title?: string;
  /** Indicates whether the image should be unoptimized */
  unoptimized?: boolean;
  /** The width of the image */
  width: number;
}

const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      alt,
      aspectRatio,
      className,
      fill = false,
      focalPoint,
      height,
      priority = false,
      src,
      srcSet,
      style,
      unoptimized,
      width,
      ...rest
    },
    ref,
  ) => {
    const aboveFold = useAboveFoldContext();

    if (!src) return null;

    const imageStyle = focalPoint
      ? {
          objectPosition: getFocalPointObjectPosition(focalPoint),
        }
      : undefined;

    const effectivePriority = Boolean(priority || aboveFold);
    const nextImageSizeProps = fill ? { fill: true } : { height, width };

    if (unoptimized) {
      return (
        <picture
          {...rest}
          className={classNames(styles.component, className, {
            [styles.isFourByThree]: aspectRatio === 'fourByThree',
            [styles.isNineBySixteen]: aspectRatio === 'nineBySixteen',
            [styles.isOneByOne]: aspectRatio === 'oneByOne',
            [styles.isSixteenByNine]: aspectRatio === 'sixteenByNine',
            [styles.isThreeByFour]: aspectRatio === 'threeByFour',
            [styles.isFilled]: fill,
          })}
          style={style}
        >
          {srcSet && (
            <React.Fragment>
              {srcSet.desktop && (
                <source
                  height={srcSet.desktop.height}
                  media={`(min-width: ${parseInt(breakpoints.desktop)}px)`}
                  srcSet={formatImageUrl(src, {
                    aspectRatio,
                    focalPoint,
                    height: srcSet.desktop.height,
                    width: srcSet.desktop.width,
                  })}
                  width={srcSet.desktop.width}
                />
              )}

              {srcSet.laptop && (
                <source
                  height={srcSet.laptop.height}
                  media={`(min-width: ${parseInt(breakpoints.laptop)}px)`}
                  srcSet={formatImageUrl(src, {
                    aspectRatio,
                    focalPoint,
                    height: srcSet.laptop.height,
                    width: srcSet.laptop.width,
                  })}
                  width={srcSet.laptop.width}
                />
              )}

              {srcSet.tablet && (
                <source
                  height={srcSet.tablet.height}
                  media={`(min-width: ${parseInt(breakpoints.tablet)}px)`}
                  srcSet={formatImageUrl(src, {
                    aspectRatio,
                    focalPoint,
                    height: srcSet.tablet.height,
                    width: srcSet.tablet.width,
                  })}
                  width={srcSet.tablet.width}
                />
              )}

              {srcSet.mobile && (
                <source
                  height={srcSet.mobile.height}
                  media={`(min-width: ${parseInt(breakpoints.mobile)}px)`}
                  srcSet={formatImageUrl(src, {
                    aspectRatio,
                    focalPoint,
                    height: srcSet.mobile.height,
                    width: srcSet.mobile.width,
                  })}
                  width={srcSet.mobile.width}
                />
              )}
            </React.Fragment>
          )}

          <img
            ref={ref}
            alt={alt}
            className={styles.image}
            {...({
              fetchPriority: effectivePriority ? 'high' : 'auto',
            } as any)}
            height={height}
            loading={effectivePriority ? 'eager' : 'lazy'}
            src={formatImageUrl(src, {
              focalPoint,
              height,
              width,
            })}
            style={imageStyle}
            width={width}
          />
        </picture>
      );
    }

    return (
      <div
        className={classNames(styles.component, className, {
          [styles.isFourByThree]: aspectRatio === 'fourByThree',
          [styles.isNineBySixteen]: aspectRatio === 'nineBySixteen',
          [styles.isOneByOne]: aspectRatio === 'oneByOne',
          [styles.isSixteenByNine]: aspectRatio === 'sixteenByNine',
          [styles.isThreeByFour]: aspectRatio === 'threeByFour',
        })}
        style={style}
      >
        <NextImage
          {...rest}
          alt={alt}
          className={styles.image}
          {...({
            fetchPriority: effectivePriority ? 'high' : 'auto',
          } as any)}
          {...nextImageSizeProps}
          priority={effectivePriority}
          src={src}
          style={imageStyle}
          unoptimized={unoptimized}
        />
      </div>
    );
  },
);

Image.displayName = 'Image';

export default Image;
